import React, { useState, useEffect, useRef, KeyboardEvent as ReactKeyboardEvent, useMemo } from 'react';
import { Image } from './ImageProvider';

interface ImagesSelectorProps {
  images: Image[];
  onSelectionComplete: (selectedImages: Image[]) => void;
  onClose: () => void;
  initialSelection?: Image[];
  singleSelect?: boolean;
}

export function ImagesSelector({ images, onSelectionComplete, onClose, initialSelection = [], singleSelect = false }: ImagesSelectorProps) {
  const [selectedImages, setSelectedImages] = useState<Image[]>(
    // If in single select mode, only keep the first selected image
    singleSelect ? initialSelection.slice(0, 1) : initialSelection
  );
  const [zoomedImage, setZoomedImage] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const imageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize refs array
  useEffect(() => {
    imageRefs.current = images.map(() => null);
  }, [images.length]);

  // Focus container on mount
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.focus();
    }
  }, []);

  // Scroll to first selected image on mount
  useEffect(() => {
    if (initialSelection.length > 0 && gridRef.current) {
      // Find the index of the first selected image
      const firstSelectedIndex = images.findIndex(img => img.id === initialSelection[0].id);
      
      if (firstSelectedIndex !== -1) {
        // Set focused index to the first selected image
        setFocusedIndex(firstSelectedIndex);
        
        // Wait for the refs to be populated
        setTimeout(() => {
          const selectedElement = imageRefs.current[firstSelectedIndex];
          if (selectedElement) {
            selectedElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
          }
        }, 100);
      }
    }
  }, [initialSelection, images]);

  const handleImageClick = (image: Image) => {
    setSelectedImages(prevSelected => {
      if (singleSelect) {
        // In single select mode, just replace the selection
        return prevSelected[0]?.id === image.id ? [] : [image];
      } else {
        // In multi-select mode, toggle the selection
        if (prevSelected.find(img => img.id === image.id)) {
          return prevSelected.filter(img => img.id !== image.id);
        } else {
          return [...prevSelected, image];
        }
      }
    });
    // Ensure container maintains focus after clicks
    containerRef.current?.focus();
  };

  const handleKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const cols = 4; // Number of columns in the grid
    const currentRow = Math.floor(focusedIndex / cols);
    const currentCol = focusedIndex % cols;
    const totalRows = Math.ceil(images.length / cols);

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        if (focusedIndex < images.length - 1) {
          setFocusedIndex(prev => prev + 1);
        }
        break;
      case 'ArrowLeft':
        e.preventDefault();
        if (focusedIndex > 0) {
          setFocusedIndex(prev => prev - 1);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (currentRow < totalRows - 1 && focusedIndex + cols < images.length) {
          setFocusedIndex(prev => prev + cols);
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (currentRow > 0) {
          setFocusedIndex(prev => prev - cols);
        }
        break;
      case 'Enter':
        e.preventDefault();
        if (e.ctrlKey) {
          handleSelectionComplete();
        } else {
          handleImageClick(images[focusedIndex]);
        }
        break;
      case ' ':
        e.preventDefault();
        setZoomedImage(images[focusedIndex].url);
        break;
      case 'Escape':
        e.preventDefault();
        if (zoomedImage) {
          setZoomedImage(null);
        } else {
          onClose();
        }
        break;
    }
  };

  const handleKeyUp = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ' && zoomedImage) {
      e.preventDefault();
      setZoomedImage(null);
    }
  };

  // Ensure focused image is visible
  useEffect(() => {
    const focusedElement = imageRefs.current[focusedIndex];
    if (focusedElement && gridRef.current) {
      focusedElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [focusedIndex]);

  const handleSelectionComplete = () => {
    onSelectionComplete(selectedImages);
    onClose();
  };

  const handleContainerClick = () => {
    // Ensure container maintains focus when clicking anywhere inside
    containerRef.current?.focus();
  };

  return (
    <div 
      ref={containerRef}
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex flex-col items-center outline-hidden"
      onKeyDown={handleKeyDown}
      onKeyUp={handleKeyUp}
      onClick={handleContainerClick}
      tabIndex={0}
    >
      <div className="absolute top-4 right-4 flex gap-2">
        <button
          onClick={onClose}
          className="text-white hover:text-gray-300"
        >
          Close
        </button>
      </div>

      <div 
        ref={gridRef}
        className="grid grid-cols-4 gap-4 overflow-auto p-4"
      >
      {images.map((image, index) => {
        const isSelected = selectedImages.some(img => img.id === image.id);
        return (
          <div
            key={image.id}
            ref={el => imageRefs.current[index] = el}
            className={`relative ${
              isSelected 
                ? 'border-4 border-blue-500' 
                : 'border-4 border-transparent'
            } ${
              focusedIndex === index 
                ? 'ring-4 ring-yellow-500' 
                : ''
            }`}
            onClick={(e) => {
              e.stopPropagation();
              handleImageClick(image);
            }}
            role="button"
            aria-pressed={isSelected}
          >
            <img
              src={image.url}
              alt={`Image ${image.id}`}
              className="w-full h-full object-contain cursor-pointer"
              referrerPolicy="no-referrer"
            />

            {isSelected && (
              <div className="absolute top-0 right-0 bg-blue-500 text-white px-2 py-1 text-xs">
                Selected
              </div>
            )}
          </div>
        )
      })}
      </div>

      <button
        onClick={(e) => {
          e.stopPropagation();
          handleSelectionComplete();
        }}
        className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-sm hover:bg-blue-700"
      >
        Done
      </button>

      {zoomedImage && (
        <div 
          className="fixed inset-0 z-60 bg-black bg-opacity-90 flex items-center justify-center"
          onClick={(e) => {
            e.stopPropagation();
            setZoomedImage(null);
          }}
        >
          <img 
            src={zoomedImage} 
            alt="Zoomed" 
            className="max-w-full max-h-full object-contain"
          />
        </div>
      )}
    </div>
  );
}
